import { useEffect, useState } from 'react';
import { pageWindow } from './pagination';

/**
 * Client-side pagination for a full in-memory row array, matching the AIM
 * table convention (0-indexed page, 25 rows/page). Resets to page 0 whenever
 * the row array reference changes (new fetch / filter) so the viewer isn't
 * stranded on a now-out-of-range page. Pairs with <AimPagination>.
 *
 * CALLER CONTRACT: `rows` must be a stable reference across renders — state,
 * or the result of a `useMemo`. Passing a freshly built array (an inline
 * `.map()` / `.filter()` / spread) makes the reset fire on every render, so
 * clicking to page 2 bounces straight back to page 0 and the table silently
 * cannot be paged at all. Audited 2026-08-11: all call sites comply.
 *
 * @param {Array} rows Full dataset. Must be referentially stable — see above.
 * @param {number} [pageSize=25] Rows per page.
 * @returns {{page:number,setPage:Function,pageCount:number,visibleRows:Array,totalItems:number}}
 */
export default function usePagedRows(rows, pageSize = 25) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [rows]);
  const { visibleRows, pageCount, safePage, totalItems } = pageWindow(rows, page, pageSize);
  return { page: safePage, setPage, pageCount, visibleRows, totalItems };
}
